import { useState, useEffect, useCallback } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { DialogModal, ConfirmPopupModal } from "../../components/ui/dialogModal";
import {
    MessageSquare, Trash2, CheckCircle2, AlertCircle, WifiOff, Loader2, Settings2,
    Plus, Star, Eye, EyeOff, Phone, Hash, Link2, ShieldCheck, Send, Inbox, Zap,
} from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { WA_ACCOUNTS_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";

// ── Providers ──────────────────────────────────────────────────────────────────

// Only the providers that can be connected from here. Convobox accounts still
// render (they are set up by hand) but are not offered in the Connect dialog.
const PROVIDERS = [
    {
        key: 'wati', label: 'WATI', monogram: 'W',
        tile: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
        blurb: 'Paste the API endpoint and bearer token from WATI → API Docs.',
    },
    {
        key: 'gallabox', label: 'Gallabox', monogram: 'G',
        tile: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
        blurb: 'Paste the API key and secret. Account, channel and number are found automatically.',
    },
];
const PROVIDER_STYLE = {
    ...Object.fromEntries(PROVIDERS.map(p => [p.key, p])),
    convobox: { label: 'Convobox', monogram: 'C', tile: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
};

const EMPTY_CONNECT = { provider: 'wati', display_name: '', wati_api_endpoint: '', wati_api_key: '', api_secret: '' };
const EMPTY_EDIT    = { display_name: '', wati_api_endpoint: '', wati_api_key: '', api_secret: '' };

// Connect needs: WATI an endpoint + bearer key; Gallabox a key + secret (the
// account, channel and number are looked up from the key by the backend).
const canConnect = (f) => f.provider === 'gallabox'
    ? !!(f.wati_api_key && f.api_secret)
    : !!(f.wati_api_endpoint && f.wati_api_key);

// 917397757432 → +91 73977 57432; anything unexpected is shown as stored.
const formatPhone = (p) => {
    const d = (p || '').replace(/\D/g, '');
    if (d.length === 12 && d.startsWith('91')) return `+91 ${d.slice(2, 7)} ${d.slice(7)}`;
    if (d.length === 10) return `+91 ${d.slice(0, 5)} ${d.slice(5)}`;
    return p || '';
};

// ── Small pieces ───────────────────────────────────────────────────────────────

const StatusPill = ({ status }) => {
    const map = {
        connected:    { cls: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-900/30 dark:text-emerald-300', dot: 'bg-emerald-500', label: 'Connected' },
        error:        { cls: 'bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-900/30 dark:text-red-300', dot: 'bg-red-500', label: 'Error' },
        disconnected: { cls: 'bg-muted text-muted-foreground ring-border', dot: 'bg-gray-400', label: 'Disconnected' },
    };
    const s = map[status] || map.disconnected;
    return (
        <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset', s.cls)}>
            <span className={cn('h-1.5 w-1.5 rounded-full', s.dot, status === 'connected' && 'animate-pulse')} />
            {s.label}
        </span>
    );
};

const ProviderTile = ({ provider, size = 'md' }) => {
    const p = PROVIDER_STYLE[provider] || PROVIDER_STYLE.wati;
    return (
        <div className={cn('flex shrink-0 items-center justify-center rounded-xl font-bold', p.tile,
            size === 'lg' ? 'h-11 w-11 text-lg' : 'h-9 w-9 text-sm')}>
            {p.monogram}
        </div>
    );
};

const Field = ({ label, required, hint, children }) => (
    <div className="space-y-1.5">
        <label className="block text-xs font-medium text-foreground">
            {label} {required && <span className="text-red-500">*</span>}
            {hint && <span className="ml-1 font-normal text-muted-foreground">{hint}</span>}
        </label>
        {children}
    </div>
);

const SecretInput = ({ value, onChange, placeholder }) => {
    const [show, setShow] = useState(false);
    return (
        <div className="relative">
            <Input type={show ? 'text' : 'password'} value={value} onChange={onChange}
                placeholder={placeholder} className="pr-9 font-mono text-xs" autoComplete="off" />
            <button type="button" onClick={() => setShow(s => !s)} tabIndex={-1}
                className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-muted-foreground hover:text-foreground"
                aria-label={show ? 'Hide' : 'Show'}>
                {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
        </div>
    );
};

const DetailRow = ({ icon: Icon, children, title }) => (
    <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground" title={title}>
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{children}</span>
    </div>
);

// ── Account card ───────────────────────────────────────────────────────────────

const AccountCard = ({ acc, onConfigure, onDisconnect, onMakePrimary, busyPrimary }) => (
    <div className={cn(
        'group relative flex flex-col rounded-2xl border bg-card p-5 transition-all hover:shadow-md',
        acc.is_primary ? 'border-primary/40 ring-1 ring-primary/20 shadow-sm' : 'border-border',
    )}>
        {acc.is_primary && (
            <span className="absolute -top-2.5 left-5 inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-foreground shadow-sm">
                <Star className="h-3 w-3 fill-current" />Primary
            </span>
        )}

        <div className="flex items-start gap-3">
            <ProviderTile provider={acc.provider} size="lg" />
            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-foreground">{acc.display_name || 'Unnamed account'}</p>
                <p className="text-xs text-muted-foreground">{acc.provider_label || 'WATI'}</p>
            </div>
            <StatusPill status={acc.status} />
        </div>

        <div className="mt-4 space-y-1.5">
            {acc.phone_number
                ? <DetailRow icon={Phone}><span className="font-medium text-foreground">{formatPhone(acc.phone_number)}</span></DetailRow>
                : <DetailRow icon={Phone}>No number on record</DetailRow>}
            {acc.wati_api_endpoint && <DetailRow icon={Link2} title={acc.wati_api_endpoint}>{acc.wati_api_endpoint}</DetailRow>}
            {acc.gallabox_channel_id && <DetailRow icon={Hash} title="Gallabox channel id">Channel {acc.gallabox_channel_id}</DetailRow>}
            {acc.convobox_org_id && <DetailRow icon={Hash} title="Convobox org id">Org {acc.convobox_org_id}</DetailRow>}
        </div>

        <div className="mt-5 flex items-center gap-2 border-t border-border pt-4">
            {acc.is_primary ? (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary">
                    <ShieldCheck className="h-3.5 w-3.5" />Used by lead page &amp; Inbox
                </span>
            ) : (
                <Button size="sm" variant="outline" onClick={() => onMakePrimary(acc)} disabled={busyPrimary}
                    className="h-8 gap-1.5 text-xs">
                    {busyPrimary ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Star className="h-3.5 w-3.5" />}
                    Make primary
                </Button>
            )}
            <div className="ml-auto flex items-center gap-1">
                <button onClick={() => onConfigure(acc)} title="Configure"
                    className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
                    <Settings2 className="h-4 w-4" />
                </button>
                <button onClick={() => onDisconnect(acc)} title="Disconnect"
                    className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20">
                    <Trash2 className="h-4 w-4" />
                </button>
            </div>
        </div>
    </div>
);

// ── Main component ─────────────────────────────────────────────────────────────

export default function WhatsAppIntegration() {
    const { fetch, create, update, remove } = useApiMethods();
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');

    const [connectOpen, setConnectOpen] = useState(false);
    const [connectForm, setConnectForm] = useState(EMPTY_CONNECT);
    const [connecting, setConnecting] = useState(false);

    const [configOpen, setConfigOpen] = useState(false);
    const [editingAccount, setEditingAccount] = useState(null);
    const [editForm, setEditForm] = useState(EMPTY_EDIT);
    const [configSaving, setConfigSaving] = useState(false);

    const [confirmDisconnect, setConfirmDisconnect] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [primaryId, setPrimaryId] = useState(null);

    const loadAccounts = useCallback(async () => {
        setLoading(true);
        const res = await fetch(WA_ACCOUNTS_URL);
        if (res?.status === 'success') {
            setAccounts(res.data || []);
            setLoadError('');
        } else {
            // A failed load used to look exactly like "no accounts connected".
            setLoadError(res?.message || 'Could not load WhatsApp accounts');
        }
        setLoading(false);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { loadAccounts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const openConnect = () => { setConnectForm(EMPTY_CONNECT); setConnectOpen(true); };

    const handleConnect = async () => {
        const isGallabox = connectForm.provider === 'gallabox';
        if (!canConnect(connectForm)) {
            ToastMsg('error', isGallabox ? 'API Key and API Secret are required' : 'WATI Endpoint and API Key are required');
            return;
        }
        setConnecting(true);
        const res = await create(WA_ACCOUNTS_URL, connectForm);
        setConnecting(false);
        if (res?.status === 'success') {
            ToastMsg('success', `WhatsApp account connected via ${isGallabox ? 'Gallabox' : 'WATI'}`);
            setConnectOpen(false);
            setConnectForm(EMPTY_CONNECT);
            loadAccounts();
        } else {
            ToastMsg('error', res?.message || 'Connection failed');
        }
    };

    const openConfig = (acc) => {
        setEditingAccount(acc);
        setEditForm({ display_name: acc.display_name, wati_api_endpoint: acc.wati_api_endpoint, wati_api_key: '', api_secret: '' });
        setConfigOpen(true);
    };

    const handleConfig = async () => {
        setConfigSaving(true);
        const payload = { id: editingAccount.id, ...editForm };
        if (!payload.wati_api_key) delete payload.wati_api_key; // don't overwrite if blank
        if (!payload.api_secret) delete payload.api_secret;
        if (editingAccount.provider === 'gallabox') delete payload.wati_api_endpoint;
        const res = await update(WA_ACCOUNTS_URL, payload);
        setConfigSaving(false);
        if (res?.status === 'success') {
            ToastMsg('success', 'Account updated');
            setConfigOpen(false);
            loadAccounts();
        } else {
            ToastMsg('error', res?.message || 'Update failed');
        }
    };

    // The lead page and Inbox send from the first account listed, and the API
    // lists the primary one first — so this decides where those messages go.
    const handleMakePrimary = async (acc) => {
        setPrimaryId(acc.id);
        const res = await update(WA_ACCOUNTS_URL, { id: acc.id, is_primary: true });
        setPrimaryId(null);
        if (res?.status === 'success') {
            ToastMsg('success', res.message || 'Primary account updated');
            loadAccounts();
        } else {
            ToastMsg('error', res?.message || 'Could not set primary');
        }
    };

    const handleDisconnect = async () => {
        const acc = confirmDisconnect;
        setDeleting(true);
        const res = await remove(`${WA_ACCOUNTS_URL}?id=${acc.id}`);
        setDeleting(false);
        setConfirmDisconnect(null);
        if (res?.status === 'success') {
            ToastMsg('success', 'Account disconnected');
            loadAccounts();
        } else {
            ToastMsg('error', res?.message || 'Failed to disconnect');
        }
    };

    const connectedCount = accounts.filter(a => a.status === 'connected').length;
    const primary = accounts.find(a => a.is_primary);
    const activeProvider = PROVIDER_STYLE[connectForm.provider];

    return (
        <div className="space-y-6 p-6">
            {/* ── Header ── */}
            <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-6">
                <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-primary/10 blur-3xl" />
                <div className="relative flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
                    <div className="flex items-start gap-4">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 text-white shadow-lg shadow-emerald-500/25">
                            <MessageSquare className="h-6 w-6" />
                        </div>
                        <div>
                            <h1 className="text-xl font-semibold tracking-tight text-foreground">WhatsApp Integration</h1>
                            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
                                Connect WhatsApp Business numbers through WATI or Gallabox. The <span className="font-medium text-foreground">primary</span> account
                                is the one the lead page and Inbox send from.
                            </p>
                        </div>
                    </div>
                    <Button onClick={openConnect} className="h-10 gap-2 self-start md:self-auto">
                        <Plus className="h-4 w-4" />Connect account
                    </Button>
                </div>

                {accounts.length > 0 && (
                    <div className="relative mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
                        {[
                            { icon: CheckCircle2, label: 'Connected', value: `${connectedCount} of ${accounts.length}` },
                            { icon: Star, label: 'Primary', value: primary ? primary.display_name : 'Not set', muted: !primary },
                            { icon: Zap, label: 'Providers', value: [...new Set(accounts.map(a => a.provider_label || 'WATI'))].join(', ') },
                        ].map(s => (
                            <div key={s.label} className="flex items-center gap-3 rounded-xl bg-muted/50 px-4 py-3">
                                <s.icon className="h-4 w-4 shrink-0 text-primary" />
                                <div className="min-w-0">
                                    <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{s.label}</p>
                                    <p className={cn('truncate text-sm font-semibold', s.muted ? 'text-amber-600 dark:text-amber-400' : 'text-foreground')}>{s.value}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* ── Body ── */}
            {loading && accounts.length === 0 ? (
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                    {[0, 1, 2].map(i => <div key={i} className="h-52 animate-pulse rounded-2xl border border-border bg-muted/40" />)}
                </div>
            ) : loadError ? (
                <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-5 dark:border-red-900/50 dark:bg-red-900/10">
                    <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
                    <div className="flex-1">
                        <p className="text-sm font-semibold text-red-700 dark:text-red-300">Couldn't load your WhatsApp accounts</p>
                        <p className="mt-0.5 text-xs text-red-600/80 dark:text-red-300/80">{loadError}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={loadAccounts}>Retry</Button>
                </div>
            ) : accounts.length === 0 ? (
                <div className="rounded-2xl border-2 border-dashed border-border bg-card px-6 py-14 text-center">
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500/10">
                        <WifiOff className="h-6 w-6 text-emerald-600" />
                    </div>
                    <h3 className="mt-4 text-base font-semibold text-foreground">No WhatsApp account yet</h3>
                    <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
                        Connect a WATI or Gallabox account to send templates from leads, run campaigns and reply in the Inbox.
                    </p>
                    <Button onClick={openConnect} className="mt-5 gap-2"><Plus className="h-4 w-4" />Connect account</Button>
                    <div className="mx-auto mt-8 grid max-w-lg grid-cols-3 gap-3 text-left">
                        {[{ icon: Send, t: 'Lead messages' }, { icon: Zap, t: 'Campaigns' }, { icon: Inbox, t: 'Shared inbox' }].map(f => (
                            <div key={f.t} className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                                <f.icon className="h-3.5 w-3.5 text-primary" />{f.t}
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <>
                    {!primary && (
                        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/10 dark:text-amber-300">
                            <Star className="mt-0.5 h-4 w-4 shrink-0" />
                            <span>No primary account is set. The lead page and Inbox use <strong>{accounts[0]?.display_name}</strong> until you pick one.</span>
                        </div>
                    )}
                    <div className="grid grid-cols-1 gap-5 pt-1 md:grid-cols-2 xl:grid-cols-3">
                        {accounts.map(acc => (
                            <AccountCard key={acc.id} acc={acc}
                                onConfigure={openConfig}
                                onDisconnect={setConfirmDisconnect}
                                onMakePrimary={handleMakePrimary}
                                busyPrimary={primaryId === acc.id} />
                        ))}
                        <button onClick={openConnect}
                            className="flex min-h-[13rem] flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-border text-muted-foreground transition-colors hover:border-primary/50 hover:bg-primary/5 hover:text-primary">
                            <Plus className="h-6 w-6" />
                            <span className="text-sm font-medium">Connect another account</span>
                        </button>
                    </div>
                </>
            )}

            {/* ── Connect dialog ── */}
            <DialogModal open={connectOpen} onClose={() => setConnectOpen(false)}
                title="Connect WhatsApp account" subtitle="Choose your provider, then paste its API credentials."
                width="520px" showActions={false}>
                <div className="space-y-5 pt-1">
                    <div className="grid grid-cols-2 gap-3">
                        {PROVIDERS.map(p => {
                            const active = connectForm.provider === p.key;
                            return (
                                <button key={p.key} type="button"
                                    onClick={() => setConnectForm(f => ({ ...f, provider: p.key }))}
                                    className={cn('relative flex items-center gap-3 rounded-xl border-2 p-3 text-left transition-all',
                                        active ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40')}>
                                    <ProviderTile provider={p.key} />
                                    <span className="text-sm font-semibold text-foreground">{p.label}</span>
                                    {active && <CheckCircle2 className="absolute right-2.5 top-2.5 h-4 w-4 text-primary" />}
                                </button>
                            );
                        })}
                    </div>

                    <p className="rounded-lg bg-muted/60 px-3 py-2 text-xs text-muted-foreground">{activeProvider?.blurb}</p>

                    <div className="space-y-4">
                        <Field label="Display name" hint={connectForm.provider === 'gallabox' ? '(optional — defaults to the channel name)' : ''}>
                            <Input value={connectForm.display_name}
                                onChange={e => setConnectForm(p => ({ ...p, display_name: e.target.value }))}
                                placeholder="e.g. Sales WhatsApp" />
                        </Field>
                        {connectForm.provider !== 'gallabox' && (
                            <Field label="API endpoint" required>
                                <Input value={connectForm.wati_api_endpoint}
                                    onChange={e => setConnectForm(p => ({ ...p, wati_api_endpoint: e.target.value }))}
                                    placeholder="https://live-mt-server.wati.io/123456" />
                            </Field>
                        )}
                        <Field label={connectForm.provider === 'gallabox' ? 'API key' : 'API key (bearer token)'} required>
                            <SecretInput value={connectForm.wati_api_key}
                                onChange={e => setConnectForm(p => ({ ...p, wati_api_key: e.target.value }))}
                                placeholder={`Paste your ${activeProvider?.label} API key`} />
                        </Field>
                        {connectForm.provider === 'gallabox' && (
                            <Field label="API secret" required>
                                <SecretInput value={connectForm.api_secret}
                                    onChange={e => setConnectForm(p => ({ ...p, api_secret: e.target.value }))}
                                    placeholder="Paste your Gallabox API secret" />
                            </Field>
                        )}
                    </div>

                    <div className="flex gap-2 border-t border-border pt-4">
                        <Button variant="outline" onClick={() => setConnectOpen(false)} className="flex-1">Cancel</Button>
                        <Button onClick={handleConnect} disabled={connecting || !canConnect(connectForm)} className="flex-1 gap-2">
                            {connecting && <Loader2 className="h-4 w-4 animate-spin" />}
                            {connecting ? 'Verifying…' : 'Verify & connect'}
                        </Button>
                    </div>
                </div>
            </DialogModal>

            {/* ── Configure dialog ── */}
            <DialogModal open={configOpen} onClose={() => setConfigOpen(false)}
                title="Configure account" subtitle={editingAccount ? `${editingAccount.provider_label || 'WATI'} · ${formatPhone(editingAccount.phone_number) || 'no number'}` : ''}
                width="480px" showActions={false}>
                <div className="space-y-4 pt-1">
                    <Field label="Display name">
                        <Input value={editForm.display_name}
                            onChange={e => setEditForm(p => ({ ...p, display_name: e.target.value }))} />
                    </Field>
                    {editingAccount?.provider !== 'gallabox' && (
                        <Field label="API endpoint">
                            <Input value={editForm.wati_api_endpoint}
                                onChange={e => setEditForm(p => ({ ...p, wati_api_endpoint: e.target.value }))}
                                placeholder="https://live-mt-server.wati.io/123456" />
                        </Field>
                    )}
                    <Field label="New API key" hint="(leave blank to keep current)">
                        <SecretInput value={editForm.wati_api_key}
                            onChange={e => setEditForm(p => ({ ...p, wati_api_key: e.target.value }))}
                            placeholder="Enter a new key to replace it" />
                    </Field>
                    {editingAccount?.provider === 'gallabox' && (
                        <Field label="New API secret" hint="(leave blank to keep current)">
                            <SecretInput value={editForm.api_secret}
                                onChange={e => setEditForm(p => ({ ...p, api_secret: e.target.value }))}
                                placeholder="Enter a new secret to replace it" />
                        </Field>
                    )}
                    <div className="flex gap-2 border-t border-border pt-4">
                        <Button variant="outline" onClick={() => setConfigOpen(false)} className="flex-1">Cancel</Button>
                        <Button onClick={handleConfig} disabled={configSaving} className="flex-1 gap-2">
                            {configSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                            Save changes
                        </Button>
                    </div>
                </div>
            </DialogModal>

            <ConfirmPopupModal
                open={!!confirmDisconnect}
                onClose={() => setConfirmDisconnect(null)}
                onConfirm={handleDisconnect}
                type="danger"
                destructive
                loading={deleting}
                title="Disconnect this account?"
                description={confirmDisconnect
                    ? `${confirmDisconnect.display_name} (${formatPhone(confirmDisconnect.phone_number) || confirmDisconnect.provider_label}) will stop sending from the CRM. Nothing changes in ${confirmDisconnect.provider_label || 'the provider'} itself.`
                    : ''}
                confirmLabel="Disconnect"
            />
        </div>
    );
}
